import { useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';
import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay';

import { usePlatformInstrumentation } from '@atlassian/platform-analytics';

import type { TrackType } from '../../api/cc-smarts/models/TrackType';
import type { ActiveContent, ContentDetails } from '../../types/activeContent';
import { useAudioPlayerStreamingContext } from '../context/AudioPlayerStreamingProvider';
import { useFlags } from '../context/FlagsProviderContext';
import { getCustomization, setSupportingContent } from '../state/AudioPlayerState';

import type { ConfluenceTrackSourceInput } from './__generated__/useContextMenuActionsAddTrackMutation.graphql';
import type { useRequeueTrackQuery } from './__generated__/useRequeueTrackQuery.graphql';
import { useAudioPlayerStore } from './useAudioPlayerStore';
import { useContextMenuActions } from './useContextMenuActions';

const messages = defineMessages({
	errorTitle: {
		id: 'ai-audio.audio-player.refetch-audio-button.error-title',
		defaultMessage: "Audio player didn't update",
		description: 'Error title shown when audio player fails to update',
	},
	errorDescription: {
		id: 'ai-audio.audio-player.refetch-audio-button.error-description',
		defaultMessage: 'Try making the changes again.',
		description: 'Error description shown when audio player fails to update',
	},
});

type AnalyticsAttributes = {
	length?: string | null;
	tone?: string | null;
	areSourcesDiverged?: boolean;
};

type UseRequeueTrackOptions = {
	analyticsAttributes?: AnalyticsAttributes;
	refetchAudio?: boolean;
};

export const useRequeueTrack = (options: UseRequeueTrackOptions = {}) => {
	const { analyticsAttributes, refetchAudio = false } = options;
	const { fetchAudio, audioRef } = useAudioPlayerStreamingContext();
	const { cloudId, activeContent, activeFeature } = useAudioPlayerStore();
	const environment = useRelayEnvironment();
	const { removeTrack, addTrack } = useContextMenuActions(cloudId);
	const { formatMessage, locale } = useIntl();
	const flags = useFlags();
	const { sendUIEvent } = usePlatformInstrumentation();

	const stopExistingAudio = useCallback(() => {
		if (audioRef?.current) {
			audioRef.current.pause();
		}
	}, [audioRef]);

	const startNewAudio = useCallback(() => {
		sendUIEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'audioRegenerate',
			source: 'audioPlayer',
			attributes: {
				version: 'v3',
				audioLocale: locale,
				...analyticsAttributes,
			},
		});
		fetchAudio();
	}, [sendUIEvent, locale, analyticsAttributes, fetchAudio]);

	const requeueTrack = useCallback(
		async (supportingContent: ContentDetails[]) => {
			if (refetchAudio) {
				stopExistingAudio();
			}

			try {
				const playlist = await fetchQuery<useRequeueTrackQuery>(
					environment,
					graphql`
						query useRequeueTrackQuery($cloudId: ID!) {
							confluence_getPlaylist(cloudId: $cloudId) {
								tracks {
									nodes {
										mainSource
										supportingSources
										type
										mainContent {
											id
										}
										trackSource
									}
								}
							}
						}
					`,
					{ cloudId },
					{ fetchPolicy: 'store-or-network' },
				).toPromise();

				if (playlist?.confluence_getPlaylist?.tracks?.nodes) {
					const activeTrackType = activeFeature.toUpperCase() as TrackType;
					const currentTrackIndex = playlist.confluence_getPlaylist.tracks.nodes.findIndex(
						(node) =>
							node?.mainContent?.id === activeContent.contentId && node?.type === activeTrackType,
					);

					if (currentTrackIndex !== -1) {
						const currentTrack = playlist.confluence_getPlaylist.tracks.nodes[currentTrackIndex];
						const trackCopy = { ...currentTrack };

						await removeTrack(
							currentTrack.mainSource,
							[...(currentTrack.supportingSources ?? [])],
							currentTrack.type as TrackType,
							undefined,
							{ showFlags: false },
						);

						await addTrack(
							trackCopy.mainSource,
							supportingContent.map((resource: ContentDetails) => resource.ari),
							trackCopy.type as TrackType,
							{
								showFlags: false,
								top: true,
								trackSource: trackCopy.trackSource as ConfluenceTrackSourceInput | undefined,
							},
						);
					}
				}

				// Update global source state only once after successful BE requeue (if applicable),
				// preventing any potential inconsistencies between the BE and local player
				setSupportingContent(supportingContent as ActiveContent['supportingContent']);

				// Reset playback speed to the set value from the customization
				if (audioRef.current) {
					audioRef.current.defaultPlaybackRate = getCustomization().playbackSpeed;
				}

				if (refetchAudio) {
					startNewAudio();
				}
			} catch {
				if (flags) {
					flags.showFlag({
						title: formatMessage(messages.errorTitle),
						description: formatMessage(messages.errorDescription),
						type: 'error',
						close: 'auto',
					});
				}
			}
		},
		[
			refetchAudio,
			stopExistingAudio,
			startNewAudio,
			environment,
			cloudId,
			activeFeature,
			activeContent.contentId,
			removeTrack,
			addTrack,
			audioRef,
			flags,
			formatMessage,
		],
	);

	return { requeueTrack };
};

